import { useEffect, useMemo, useState } from 'react'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import { Stack, Typography, styled, keyframes } from '@mui/material'
import {
  useGetDocumentsCount,
  useGetDocumentsExist,
} from '@/service-library/hooks/documents'
import ExpandAndGrow from '../expand-and-grow/ExpandAndGrow'
import OutlinedCard from '../outlined-card/OutlinedCard'

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
`

const AnimatedGradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, 
    ${theme.palette.text.primary} 0%, 
    ${theme.palette.primary.main} 15%, 
    ${theme.palette.info.main} 50%, 
    ${theme.palette.primary.main} 85%, 
    ${theme.palette.text.primary} 100%)`,
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: `${gradientAnimation} 3.5s linear infinite`,
}))

const textVariations = [
  'Hang tight',
  "AI's on it",
  'Working on it',
  'AI magic at work',
  'Almost there',
  'Processing with AI',
  'AI at work',
  'AI automation in progress',
  'AI hard at work',
  'AI has this handled',
  'Just a moment',
]

const autoAssigningDocumentsFilters = {
  fields__only: 'id,name',
  upload_status: 'ai_routing_to_project',
  project_id__isnull: 'true',
}

export default function AutoAssigningDocumentsCard() {
  const [shouldCheckExists, setShouldCheckExists] = useState(true)

  const { exists } = useGetDocumentsExist({
    filters: autoAssigningDocumentsFilters,
    refetchInterval: 5000, // Temporary until we have notifications
    enabled: shouldCheckExists,
  })
  const { documentsCount } = useGetDocumentsCount({
    filters: autoAssigningDocumentsFilters,
    refetchInterval: 2000, // Temporary until we have notifications
    enabled: exists,
  })

  // While we know there are documents being auto-assigned, no need to check for existence
  useEffect(() => {
    setShouldCheckExists(documentsCount === 0)
  }, [documentsCount])

  // Select a random text variation on component mount.
  const variationIndex = useMemo(() => {
    return Math.floor(Math.random() * textVariations.length)
  }, [])

  return (
    <ExpandAndGrow show={documentsCount > 0} expandedHeight={38}>
      <OutlinedCard color="info.main">
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesome fontSize="small" />
            <AnimatedGradientText variant="body2">
              {textVariations[variationIndex]} - Assigning{' '}
              <b>{documentsCount}</b>{' '}
              {documentsCount !== 1 ? 'documents' : 'document'} to projects
            </AnimatedGradientText>
          </Stack>
        </Stack>
      </OutlinedCard>
    </ExpandAndGrow>
  )
}
